// ─── Administration console route (ADR 0035, issue #451) ───
// /app/admin — the second door. Settings kept the personal surface
// ("Tú"); every system-administration view moves HERE, navigated by a
// vertical rail with GROUP HEADERS (D4: flat 9-view strips reproduce the
// original #451 problem one door deeper; settings-heavy products go
// vertical with groups at this size).
//
// Phase 1 (this file's birth) carries the five superadmin-only views:
// Empresas · Calendarios · Conocimientos (builder) · Administración ·
// Eliminados. Phase 2 adds the manager views (Clientes, Tipos de tarea,
// Alertas) and widens `canSeeAdminConsole`; phase 3 lands the Acceso
// console and the Máquinas manage view. A group whose views are all
// invisible to the caller does not render at all.
//
// Entry-point contract (D2): this route ships in the SAME PR as its
// sidebar/user-menu entries — a route reachable by nothing is the
// unreachable-feature failure this repo has already shipped once. The
// guard mirrors /app/knowledge's render-time <Navigate> pattern; an
// unauthorized visitor lands on /app/settings (their own door), never a
// 403 page. The guard is UX only — every view's server endpoint
// re-checks authority per request.
//
// URL contract: `?tab=` keeps settings' param name so the permanent
// redirects (old emails hold `/app/settings?tab=companies` forever —
// D10) are a verbatim passthrough. `questionnaireStatus` moved here with
// the Conocimientos builder (its filter hook now binds to this route).

import { createFileRoute, Navigate, useNavigate, useSearch } from '@tanstack/react-router';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import {
  BellRing,
  Briefcase,
  CalendarDays,
  GraduationCap,
  Landmark,
  Lock,
  Monitor,
  Shield,
  UsersRound,
  GitBranch,
  Tags,
  Trash2,
  type LucideIcon,
  Mail,
  Layers,
} from 'lucide-react';
import type { AuthenticatedUser } from '@a8-planner/shared';
import { useAuth } from '../../features/auth/AuthProvider';
import { canSeeAdminConsole } from '../../features/admin/visibility';
import { Section } from '../../features/settings/Section';
import { WorkspaceManagement } from '../../features/settings/WorkspaceManagement';
import { FailedEmailsPanel } from '../../features/settings/FailedEmailsPanel';
import { DeletedItemsPanel } from '../../features/settings/DeletedItemsPanel';
import { CompaniesTab } from '../../features/companies/CompaniesTab';
import { CalendarsTab } from '../../features/calendars/CalendarsTab';
import { KnowledgeTab } from '../../features/knowledge/KnowledgeTab';
import { ClientsTab } from '../../features/clients/ClientsTab';
import { TaskTypesPanel } from '../../features/settings/TaskTypesPanel';
import { SpecialtiesPanel } from '../../features/settings/SpecialtiesPanel';
import { PhaseTemplatesPanel } from '../../features/projects/PhaseTemplatesPanel';
import { EmailSettingsPanel } from '../../features/settings/EmailSettingsPanel';
import { DevOpsEstadosPanel } from '../../features/settings/DevOpsEstadosPanel';
import { useActiveCompany } from '../../features/companies/useActiveCompany';
import { AlertsAdminMatrix } from '../../features/alerts/AlertsAdminMatrix';
import { AccessConsole } from '../../features/access/AccessConsole';
import { MyPeoplePanel } from '../../features/access/MyPeoplePanel';
import { DevMachinesTab } from '../../features/dev-machines/DevMachinesTab';

/** Every console view id — the single source for the search schema AND the
 * redirect-contract test (admin-settings-redirect.test.ts), which pins this
 * list to settings' `TABS_MOVED_TO_ADMIN` so the two files can never drift. */
export const ADMIN_VIEW_IDS = [
  'companies',
  'calendars',
  'knowledge',
  'admin',
  'deleted',
  'clients',
  'task-types',
  'phase-templates', // ADR 0039 D4 (#1200)
  'alerts',
  'dev-machines',
  'access',
  'system-email', // GHE #1086
  'devops-estados', // GHE #1125
  'specialties', // GHE #1291
  'my-people', // GHE #1298
] as const;

/** Console views with NO settings-redirect ancestor. `access` is a SPLIT, not
 * a move (ADR 0035 D7): settings keeps `?tab=access` as the personal
 * "Mi acceso" view, while THIS view is the management console — so the
 * redirect contract is moved ∪ native = all, not moved = all. */
// GHE #1086 — `system-email` is NATIVE, not moved: it never existed under settings, so there
// is no `?tab=` to redirect from. Listing it here is what keeps moved ∪ native = every view.
// GHE #1125 — `devops-estados` también es NATIVA: nunca existió bajo settings, así que no hay
// ningún `?tab=` del que redirigir.
export const ADMIN_NATIVE_VIEW_IDS = [
  'access',
  'system-email',
  'devops-estados',
  // GHE #1298 — nace en la consola: nunca vivió bajo settings, así que no hay nada que redirigir.
  'my-people',
  // ADR 0039 D4 (#1200) — born in the console: it never lived in settings, so nothing redirects here.
  'phase-templates',
  // GHE #1291 — nace aquí igual: nunca vivió en Configuración, así que no hay nada que redirigir.
  'specialties',
] as const;

export const adminSearchSchema = z.object({
  /** Which console view is open. Same param name as settings' `?tab=` so the
   *  permanent redirects are a verbatim passthrough (ADR 0035 D10). */
  tab: z.enum(ADMIN_VIEW_IDS).optional().catch(undefined),
  /** The Conocimientos builder's questionnaire status filter — moved here
   *  with the builder (was a settings param; GHE #448 history). */
  questionnaireStatus: z.enum(['DRAFT', 'PUBLISHED', 'ARCHIVED']).optional().catch(undefined),
  /**
   * The Clients view's debounced search text — moved here with the view
   * (phase 2). ⚠️ #445 in reverse, copied with the view: free text must stay
   * a STRING (searching `8` means the text "8"), but a hand-typed or
   * truncated `?clientSearch=8` arrives from TanStack's parser as a NUMBER,
   * and a bare `z.string()` would reject it and silently drop a search the
   * user can see in their own URL.
   */
  clientSearch: z
    .union([z.string(), z.number(), z.boolean()])
    .optional()
    .catch(undefined)
    .transform((v): string | undefined => (v === undefined ? undefined : String(v))),
  /** Deep-link into a machine's detail panel — moved here with the Máquinas
   *  manage view (phase 3; was `?tab=dev-machines&machineId=…` on settings). */
  machineId: z.string().uuid().optional().catch(undefined),
});

export const Route = createFileRoute('/app/admin')({
  validateSearch: adminSearchSchema,
  component: AdminConsoleGuard,
});

type AdminView = NonNullable<z.infer<typeof adminSearchSchema>['tab']>;

// ─── Rail configuration (ADR 0035 D4) ───
// Three fixed groups. Views declare which group they sit in; a group with no
// visible views vanishes. Labels reuse the settings namespace — the moved
// views keep their existing `tabs.*` keys, so nothing is retranslated.

interface AdminViewItem {
  id: AdminView;
  labelKey:
    | 'tabs.companies'
    | 'tabs.calendars'
    | 'tabs.knowledge'
    | 'tabs.specialties'
    | 'tabs.myPeople'
    | 'tabs.admin'
    | 'tabs.deleted'
    | 'tabs.clients'
    | 'tabs.taskTypes'
    | 'tabs.phaseTemplates'
    | 'tabs.alerts'
    | 'tabs.devMachines'
    | 'tabs.access'
    | 'tabs.systemEmail'
    | 'tabs.devopsEstados';
  icon: LucideIcon;
  /** True = superadmin only; false = every console visitor (manager ∪
   * superadmin — `canSeeAdminConsole` already gates the door). UX-visibility
   * only; the server re-checks per request. */
  superadminOnly: boolean;
}

interface AdminGroup {
  id: 'people' | 'catalogs' | 'system';
  labelKey: 'console.groups.people' | 'console.groups.catalogs' | 'console.groups.system';
  views: AdminViewItem[];
}

// View order within groups follows ADR 0035 D4's table verbatim.
const ADMIN_GROUPS: AdminGroup[] = [
  {
    id: 'people',
    labelKey: 'console.groups.people',
    views: [
      { id: 'access', labelKey: 'tabs.access', icon: Lock, superadminOnly: false },
      // GHE #1298 — la vista del GESTOR. `superadminOnly: false` no es un descuido: es el punto.
      // «Acceso → Personas» es superadmin y le devuelve 403, así que sin esto tenía el permiso de
      // poner responsable y ningún sitio desde el que verlo.
      { id: 'my-people', labelKey: 'tabs.myPeople', icon: UsersRound, superadminOnly: false },
    ],
  },
  {
    id: 'catalogs',
    labelKey: 'console.groups.catalogs',
    views: [
      { id: 'clients', labelKey: 'tabs.clients', icon: Briefcase, superadminOnly: false },
      { id: 'task-types', labelKey: 'tabs.taskTypes', icon: Tags, superadminOnly: false },
      // GHE #1291 — las áreas de conocimiento de un workspace. `superadminOnly: false` como sus
      // vecinas: las administra el GESTOR del workspace, y el servidor lo re-comprueba en cada
      // petición (RBAC en las dos capas), así que esconder la entrada es comodidad, nunca la
      // verja de verdad.
      {
        id: 'specialties',
        labelKey: 'tabs.specialties',
        icon: GraduationCap,
        superadminOnly: false,
      },
      {
        id: 'phase-templates',
        labelKey: 'tabs.phaseTemplates',
        icon: Layers,
        superadminOnly: false,
      },
      { id: 'dev-machines', labelKey: 'tabs.devMachines', icon: Monitor, superadminOnly: false },
      { id: 'calendars', labelKey: 'tabs.calendars', icon: CalendarDays, superadminOnly: true },
      { id: 'knowledge', labelKey: 'tabs.knowledge', icon: GraduationCap, superadminOnly: true },
      { id: 'companies', labelKey: 'tabs.companies', icon: Landmark, superadminOnly: true },
    ],
  },
  {
    id: 'system',
    labelKey: 'console.groups.system',
    views: [
      { id: 'alerts', labelKey: 'tabs.alerts', icon: BellRing, superadminOnly: false },
      // GHE #1086 — superadminOnly, matching the server: EmailSettingsService asserts it too
      // (RBAC at BOTH layers), so hiding the tab is convenience, never the actual gate.
      { id: 'system-email', labelKey: 'tabs.systemEmail', icon: Mail, superadminOnly: true },
      {
        id: 'devops-estados',
        labelKey: 'tabs.devopsEstados',
        icon: GitBranch,
        superadminOnly: true,
      },
      { id: 'admin', labelKey: 'tabs.admin', icon: Shield, superadminOnly: true },
      { id: 'deleted', labelKey: 'tabs.deleted', icon: Trash2, superadminOnly: true },
    ],
  },
];

function isViewVisible(view: AdminViewItem, isSuperAdmin: boolean): boolean {
  return view.superadminOnly ? isSuperAdmin : true;
}

/** The view ids this user may open, in rail order — drives the rail, the
 * mobile nav AND the deep-link fallback, so the three can never disagree.
 * Exported for the visibility test. */
export function visibleAdminViewIds(user: AuthenticatedUser | null | undefined): AdminView[] {
  const isSuperAdmin = !!user?.isSuperAdmin;
  return ADMIN_GROUPS.flatMap((g) => g.views)
    .filter((v) => isViewVisible(v, isSuperAdmin))
    .map((v) => v.id);
}

// ─── View content ───
// The same thin Section wrappers the settings route used to render — the
// feature components themselves (CompaniesTab, KnowledgeTab, …) move
// untouched; only their address changed.

function ViewContent({
  view,
  machineId,
}: {
  view: AdminView;
  machineId?: string;
}): React.ReactElement | null {
  const { t } = useTranslation('settings');
  switch (view) {
    case 'companies':
      return (
        <Section title={t('companiesTab.title')} description={t('companiesTab.description')}>
          <CompaniesTab />
        </Section>
      );
    case 'calendars':
      return (
        <Section title={t('calendarsTab.title')} description={t('calendarsTab.description')}>
          <CalendarsTab />
        </Section>
      );
    case 'knowledge':
      // The builder renders its own internal heading and scales with width —
      // no Section wrapper, matching its settings-era rendering.
      return <KnowledgeTab />;
    case 'admin':
      return (
        <div className="flex flex-col gap-8">
          <Section title={t('adminTab.title')} description={t('adminTab.description')}>
            <WorkspaceManagement />
          </Section>
          <Section
            title={t('adminTab.failedEmails.sectionTitle')}
            description={t('adminTab.failedEmails.sectionDescription')}
          >
            <FailedEmailsPanel />
          </Section>
        </div>
      );
    case 'deleted':
      return (
        <Section title={t('deletedTab.title')} description={t('deletedTab.description')}>
          <DeletedItemsPanel />
        </Section>
      );
    case 'clients':
      return (
        <Section title={t('clientsTab.title')} description={t('clientsTab.description')}>
          <ClientsTab />
        </Section>
      );
    case 'task-types':
      return <TaskTypesViewContent />;
    // GHE #1291 — sin `Section` envolvente: el panel trae su propio encabezado porque cambia de
    // modo (lista ⇄ editor) y el título tiene que cambiar con él.
    case 'specialties':
      return <SpecialtiesPanel />;
    case 'phase-templates':
      return <PhaseTemplatesViewContent />;
    case 'system-email':
      return (
        <Section title={t('emailTab.title')} description={t('emailTab.description')}>
          <EmailSettingsPanel />
        </Section>
      );
    case 'devops-estados':
      return (
        <Section
          title={t('devopsEstadosTab.title')}
          description={t('devopsEstadosTab.description')}
        >
          <DevOpsEstadosPanel />
        </Section>
      );
    case 'alerts':
      return (
        <Section title={t('alertsTab.title')} description={t('alertsTab.description')}>
          <AlertsAdminMatrix />
        </Section>
      );
    case 'my-people':
      return (
        <Section title={t('myPeopleTab.title')} description={t('myPeopleTab.description')}>
          <MyPeoplePanel />
        </Section>
      );
    case 'access':
      // The management console half of the D7 split — Workspaces/Spaces
      // (managers) + People/Activity (superadmin). The personal "Mi acceso"
      // half stayed on /app/settings; AccessConsole no longer carries it.
      return (
        <Section title={t('accessTab.title')} description={t('accessTab.description')}>
          <AccessConsole defaultTab="workspaces" />
        </Section>
      );
    case 'dev-machines':
      return (
        <Section title={t('devMachinesTab.title')} description={t('devMachinesTab.description')}>
          <DevMachinesTab initialMachineId={machineId} />
        </Section>
      );
    default:
      return null;
  }
}

// Its own component (not a switch case body) because it calls a hook —
// same shape as its settings-era wrapper.
// ADR 0039 D4 (#1200) — the phase-template catalog, governed exactly like the task types beside it.
function PhaseTemplatesViewContent(): React.ReactElement {
  const { t } = useTranslation('settings');
  const { activeCompany } = useActiveCompany();
  return (
    <Section title={t('phaseTemplatesTab.title')} description={t('phaseTemplatesTab.description')}>
      <PhaseTemplatesPanel companyId={activeCompany?.id ?? null} />
    </Section>
  );
}

function TaskTypesViewContent(): React.ReactElement {
  const { t } = useTranslation('settings');
  const { activeCompany } = useActiveCompany();
  return (
    <Section title={t('taskTypesTab.title')} description={t('taskTypesTab.description')}>
      <TaskTypesPanel companyId={activeCompany?.id ?? null} />
    </Section>
  );
}

// ─── Rail (desktop) + horizontal nav (mobile) ───

interface AdminNavProps {
  activeView: AdminView;
  onViewChange: (view: AdminView) => void;
  isSuperAdmin: boolean;
}

function AdminRail({ activeView, onViewChange, isSuperAdmin }: AdminNavProps): React.ReactElement {
  const { t } = useTranslation('settings');
  const groups = ADMIN_GROUPS.map((g) => ({
    ...g,
    views: g.views.filter((v) => isViewVisible(v, isSuperAdmin)),
  }));
  return (
    <nav aria-label={t('console.nav.ariaLabel')} className="flex flex-col gap-5">
      {groups
        .filter((g) => g.views.length > 0)
        .map((group) => (
          <div key={group.id}>
            <p className="mb-1.5 px-3 text-xs font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">
              {t(group.labelKey)}
            </p>
            <div className="flex flex-col gap-1">
              {group.views.map(({ id, labelKey, icon: Icon }) => {
                const isActive = activeView === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => onViewChange(id)}
                    aria-current={isActive ? 'page' : undefined}
                    className={`flex coarse:min-h-[44px] items-center gap-2.5 rounded-md px-3 py-2 text-left text-sm transition-colors duration-150 focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)] ${
                      isActive
                        ? 'bg-surface-raised font-medium text-[var(--text-primary)]'
                        : 'text-[var(--text-secondary)] hover:bg-surface-raised hover:text-[var(--text-primary)]'
                    }`}
                  >
                    <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                    {t(labelKey)}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
    </nav>
  );
}

function MobileAdminNav({
  activeView,
  onViewChange,
  isSuperAdmin,
}: AdminNavProps): React.ReactElement {
  const { t } = useTranslation('settings');
  const views = ADMIN_GROUPS.flatMap((g) => g.views).filter((v) => isViewVisible(v, isSuperAdmin));
  return (
    <nav aria-label={t('console.nav.ariaLabel')} className="-mx-1 overflow-x-auto">
      <div className="flex gap-1 px-1 pb-1">
        {views.map(({ id, labelKey, icon: Icon }) => {
          const isActive = activeView === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => onViewChange(id)}
              aria-current={isActive ? 'page' : undefined}
              className={`flex coarse:min-h-[44px] shrink-0 items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors duration-150 focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)] ${
                isActive
                  ? 'border-border bg-surface-raised font-medium text-[var(--text-primary)]'
                  : 'border-transparent text-[var(--text-secondary)] hover:bg-surface-raised'
              }`}
            >
              <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
              {t(labelKey)}
            </button>
          );
        })}
      </div>
    </nav>
  );
}

// ─── Page ───

function AdminConsoleGuard(): React.ReactElement {
  const { user } = useAuth();
  // Unauthorized (or role revoked mid-session) → their own door, never a 403
  // page. No loop with settings' moved-tab redirect: settings only redirects
  // HERE for tabs this guard's audience can open, and the bounce back drops
  // the tab param.
  if (!canSeeAdminConsole(user)) return <Navigate to="/app/settings" replace />;
  return <AdminConsolePage />;
}

function AdminConsolePage(): React.ReactElement {
  const { t } = useTranslation('settings');
  const { user } = useAuth();
  const { tab, machineId } = useSearch({ from: '/app/admin' });
  const navigate = useNavigate({ from: '/app/admin' });

  const isSuperAdmin = !!user?.isSuperAdmin;
  // A deep link to a view this visitor may not open (a manager following a
  // superadmin's ?tab=companies link) falls back to their FIRST visible view
  // — never a blank panel, never the hidden view. Same derivation the rail
  // renders from, so the fallback is always a button they can see.
  const visible = visibleAdminViewIds(user);
  const activeView: AdminView = tab && visible.includes(tab) ? tab : (visible[0] ?? 'clients');

  const handleViewChange = (view: AdminView): void => {
    // `replace: true` — lateral moves within the console are not navigation
    // worth stepping back through one entry at a time (settings precedent).
    // A view switch also drops view-scoped params (questionnaireStatus is
    // the builder's, not the console's).
    void navigate({ search: { tab: view }, replace: true });
  };

  // GHE #452 — no Breadcrumb. The console is a top-level destination, and its
  // only possible segment was `t('console.title')`, the exact string the <h1>
  // below already renders.
  return (
    <div className="min-h-full px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto w-full max-w-[1600px]">
        <div className="mb-8">
          <div className="mb-1 flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-surface-raised">
              <Shield className="h-5 w-5 text-[var(--text-secondary)]" aria-hidden="true" />
            </div>
            <h1 className="text-2xl font-semibold text-[var(--text-primary)]">
              {t('console.title')}
            </h1>
          </div>
          <p className="ml-12 text-sm text-[var(--text-secondary)]">{t('console.description')}</p>
        </div>

        <div className="mb-6 lg:hidden">
          <MobileAdminNav
            activeView={activeView}
            onViewChange={handleViewChange}
            isSuperAdmin={isSuperAdmin}
          />
        </div>

        <div className="flex gap-8">
          <aside className="hidden w-52 flex-shrink-0 lg:block">
            <AdminRail
              activeView={activeView}
              onViewChange={handleViewChange}
              isSuperAdmin={isSuperAdmin}
            />
          </aside>
          {/* Plain div, not <main> — AppShell renders the page's one <main>
                landmark around the Outlet (#393). */}
          <div className="min-w-0 flex-1">
            <ViewContent view={activeView} machineId={machineId} />
          </div>
        </div>
      </div>
    </div>
  );
}
